
import React, { Fragment, useMemo, useRef, useState } from 'react'

import { Box, Divider, IconButton, TablePagination, TablePaginationProps } from '@material-ui/core'

import { FirstPage, LastPage, KeyboardArrowLeft, KeyboardArrowRight } from '../../icons'

import { makeAppStyles } from '../../themes'

import { SimpleTable, SimpleTableProps } from './SimpleTable'


interface TablePaginationActionsProps {
  count: number
  page: number
  rowsPerPage: number
  onChangePage: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void
}

export const TablePaginationActions = (props: TablePaginationActionsProps): JSX.Element => {

  const { count, page, rowsPerPage, onChangePage } = props

  const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onChangePage(event, 0)
  }

  const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onChangePage(event, page - 1)
  }

  const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onChangePage(event, page + 1)
  }

  const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    onChangePage(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))
  }

  return (
    <Box fontSize={14} px={1} display='flex' alignItems='flexCenter' justifyContent='flex-end'>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page">
        <FirstPage />
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page">
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page">
        <KeyboardArrowRight />
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page">
        <LastPage />
      </IconButton>
    </Box>
  )
}


const useStyles = makeAppStyles( theme => ({
  label: {
    fontSize: 14,
    fontWeight: theme.typography.fontWeightBold,
  },
}))


export type PaginatedTableProps<T extends object> = (
  SimpleTableProps<T> & {
    paginate: number
    TablePaginationProps?: Partial<
      Omit<TablePaginationProps, 'ref' | 'count' | 'rowsPerPage' | 'page' | 'onChangePage' | 'rowsPerPageOptions'>
    >
  }
)

export function PaginatedTable<T extends object>({
  paginate,
  rows,
  TableContainerProps = {},
  TablePaginationProps = {},
  ...props
}: PaginatedTableProps<T>): JSX.Element {

  const [page, setPage] = useState(0)

  const items = useMemo(() => {
    const offset = page * paginate
    return paginate >= rows.length ?
      rows :
      rows.slice(offset, Math.min(offset + paginate, rows.length))
  }, [paginate, rows, page])

  const classes = useStyles()

  const tableContainerRef = useRef<HTMLTableElement | null>(null)

  return (
    <Fragment>

      <SimpleTable
        rows={items}
        {...props}
        TableContainerProps={{
          ref: tableContainerRef,
          ...TableContainerProps,
        }} />

      <Divider />

      <TablePagination
        component='div'
        ActionsComponent={TablePaginationActions}
        labelDisplayedRows={(pageInfo): JSX.Element => (
          <span className={classes.label}>
            {`${pageInfo.from} - ${pageInfo.to} of ${pageInfo.count}`}
          </span>
        )}
        {...TablePaginationProps}
        count={rows.length}
        rowsPerPage={paginate}
        rowsPerPageOptions={[paginate]}
        page={page}
        onChangePage={(e, newPage): void => {
          setPage(newPage)
          if( tableContainerRef.current ){
            tableContainerRef.current.scrollTo({ top: 0 })
          }
        }} />

    </Fragment>
  )
}  